import React, { useState, useMemo, useRef } from 'react'
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Badge,
  InputGroup,
  Modal,
  Tabs,
  Tab,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap'
import {
  Search,
  Plus,
  Check,
  Ship,
  Truck,
  Plane,
  Target,
  Lightbulb,
  Edit2,
  Copy,
  Trash2,
  Eye,
  Move,
  Info,
  RotateCcw,
  RotateCw,
  Waves,
  Mountain,
  Cloud,
  AlertTriangle,
} from 'lucide-react'
import { SelectionContainer } from '../styles'

interface MilitaryObject {
  id: string
  name: string
  category: 'ship' | 'vehicle' | 'aircraft'
  subcategory: string
  description: string
  domain: 'naval' | 'ground' | 'air'
  tags: string[]
  doctrine?: string
}

interface ObjectGroup {
  id: string
  name: string
  description: string
  objects: string[]
  targetObjects: string[]
  type: 'catalog' | 'my-group'
  doctrine?: string
}

interface ObjectSelectionProps {
  onNext: (groups: ObjectGroup[], selectedObjects: MilitaryObject[]) => void
}

// Expanded Mock Data
const MILITARY_OBJECTS: MilitaryObject[] = [
  // Naval Objects
  {
    id: 'destroyer-1',
    name: 'Type 052D Destroyer',
    category: 'ship',
    subcategory: 'Destroyer',
    description: 'Advanced guided-missile destroyer with multi-layered air defense capabilities',
    domain: 'naval',
    tags: ['Surface Combatant', 'Guided Missile'],
    doctrine: 'Chinese',
  },
  {
    id: 'frigate-1',
    name: 'Type 054A Frigate',
    category: 'ship',
    subcategory: 'Frigate',
    description: 'Multi-role frigate optimized for anti-submarine and escort missions',
    domain: 'naval',
    tags: ['Surface Combatant', 'Multi-Role'],
    doctrine: 'Chinese',
  },
  {
    id: 'carrier-1',
    name: 'Liaoning Aircraft Carrier',
    category: 'ship',
    subcategory: 'Aircraft Carrier',
    description: 'Ski-jump aircraft carrier capable of deploying fighter aircraft and helicopters',
    domain: 'naval',
    tags: ['Capital Ship', 'Aviation'],
    doctrine: 'Chinese',
  },
  {
    id: 'submarine-1',
    name: 'Type 093 Nuclear Submarine',
    category: 'ship',
    subcategory: 'Nuclear Submarine',
    description: 'Nuclear-powered attack submarine with long-range strike capabilities',
    domain: 'naval',
    tags: ['Submarine', 'Attack'],
    doctrine: 'Chinese',
  },
  {
    id: 'corvette-1',
    name: 'Type 056 Corvette',
    category: 'ship',
    subcategory: 'Corvette',
    description: 'Coastal patrol vessel designed for littoral operations and security missions',
    domain: 'naval',
    tags: ['Patrol Vessel', 'Coastal'],
    doctrine: 'Chinese',
  },
  {
    id: 'cruiser-1',
    name: 'Type 055 Cruiser',
    category: 'ship',
    subcategory: 'Cruiser',
    description: 'Large destroyer-class vessel with advanced radar and long-range missile systems',
    domain: 'naval',
    tags: ['Surface Combatant', 'Heavy'],
    doctrine: 'Chinese',
  },

  // Ground Vehicles
  {
    id: 'tank-1',
    name: 'Type 99A Main Battle Tank',
    category: 'vehicle',
    subcategory: 'Main Battle Tank',
    description:
      'Third-generation main battle tank with advanced fire control and protection systems',
    domain: 'ground',
    tags: ['Heavy Armor', 'Tracked'],
    doctrine: 'Chinese',
  },
  {
    id: 'ifv-1',
    name: 'Type 04A Infantry Fighting Vehicle',
    category: 'vehicle',
    subcategory: 'Infantry Fighting Vehicle',
    description:
      'Amphibious infantry fighting vehicle with troop transport and fire support capabilities',
    domain: 'ground',
    tags: ['Light Armor', 'Tracked'],
    doctrine: 'Chinese',
  },
  {
    id: 'apc-1',
    name: 'Type 08 Armored Personnel Carrier',
    category: 'vehicle',
    subcategory: 'Armored Personnel Carrier',
    description: 'Wheeled armored vehicle designed for rapid troop deployment and transport',
    domain: 'ground',
    tags: ['Transport', 'Wheeled'],
    doctrine: 'Chinese',
  },
  {
    id: 'truck-1',
    name: 'Dongfeng Military Truck',
    category: 'vehicle',
    subcategory: 'Military Truck',
    description: 'Heavy-duty logistics vehicle for cargo transport and equipment hauling',
    domain: 'ground',
    tags: ['Logistics', 'Wheeled'],
    doctrine: 'Chinese',
  },
  {
    id: 'artillery-1',
    name: 'PLZ-05 Self-Propelled Howitzer',
    category: 'vehicle',
    subcategory: 'Artillery',
    description: 'Self-propelled howitzer with automated loading and digital fire control systems',
    domain: 'ground',
    tags: ['Artillery', 'Tracked'],
    doctrine: 'Chinese',
  },
  {
    id: 'rocket-1',
    name: 'PHL-03 Multiple Rocket Launcher',
    category: 'vehicle',
    subcategory: 'Rocket Artillery',
    description: 'Mobile multiple rocket launcher system for area bombardment missions',
    domain: 'ground',
    tags: ['Artillery', 'Wheeled'],
    doctrine: 'Chinese',
  },

  // Aircraft
  {
    id: 'fighter-1',
    name: 'J-20 Stealth Fighter',
    category: 'aircraft',
    subcategory: 'Fighter',
    description:
      'Fifth-generation stealth fighter aircraft with air-to-air and air-to-ground capabilities',
    domain: 'air',
    tags: ['Stealth', 'Air Superiority'],
    doctrine: 'Chinese',
  },
  {
    id: 'bomber-1',
    name: 'H-6K Strategic Bomber',
    category: 'aircraft',
    subcategory: 'Bomber',
    description:
      'Long-range strategic bomber capable of carrying cruise missiles and precision munitions',
    domain: 'air',
    tags: ['Strategic', 'Long Range'],
    doctrine: 'Chinese',
  },
  {
    id: 'helicopter-1',
    name: 'Z-10 Attack Helicopter',
    category: 'aircraft',
    subcategory: 'Attack Helicopter',
    description:
      'Dedicated attack helicopter with anti-tank missiles and close air support capabilities',
    domain: 'air',
    tags: ['Close Support', 'Anti-Tank'],
    doctrine: 'Chinese',
  },
  {
    id: 'transport-1',
    name: 'Y-20 Transport Aircraft',
    category: 'aircraft',
    subcategory: 'Transport',
    description: 'Heavy transport aircraft for strategic airlift and cargo delivery missions',
    domain: 'air',
    tags: ['Cargo', 'Strategic Lift'],
    doctrine: 'Chinese',
  },
  {
    id: 'drone-1',
    name: 'Wing Loong II Drone',
    category: 'aircraft',
    subcategory: 'UAV',
    description:
      'Medium-altitude long-endurance UAV for surveillance and precision strike missions',
    domain: 'air',
    tags: ['Reconnaissance', 'Strike'],
    doctrine: 'Chinese',
  },
  {
    id: 'awacs-1',
    name: 'KJ-2000 AWACS',
    category: 'aircraft',
    subcategory: 'AWACS',
    description:
      'Airborne early warning and control system for air battle management and surveillance',
    domain: 'air',
    tags: ['Command & Control', 'Surveillance'],
    doctrine: 'Chinese',
  },
]

const PRESET_GROUPS: ObjectGroup[] = [
  // NAVAL GROUPS (Water biome spawning)
  {
    id: 'naval-strike-group',
    name: 'Naval Strike Group',
    description: 'Complete naval battle group for deep water operations',
    objects: ['carrier-1', 'destroyer-1', 'frigate-1', 'submarine-1', 'corvette-1', 'cruiser-1'],
    targetObjects: ['carrier-1', 'destroyer-1', 'cruiser-1'],
    type: 'catalog',
    doctrine: 'Chinese',
  },
  {
    id: 'submarine-wolf-pack',
    name: 'Submarine Wolf Pack',
    description: 'Coordinated submarine formation for sea lane interdiction',
    objects: ['submarine-1', 'corvette-1', 'frigate-1'],
    targetObjects: ['submarine-1'],
    type: 'catalog',
    doctrine: 'Chinese',
  },
  {
    id: 'coastal-patrol',
    name: 'Coastal Patrol Fleet',
    description: 'Light naval vessels for coastal security operations',
    objects: ['corvette-1', 'frigate-1'],
    targetObjects: ['corvette-1', 'frigate-1'],
    type: 'catalog',
    doctrine: 'Chinese',
  },

  // GROUND GROUPS (Land biome spawning)
  {
    id: 'armored-division',
    name: 'Armored Division',
    description: 'Heavy mechanized formation for ground assault',
    objects: ['tank-1', 'ifv-1', 'apc-1', 'artillery-1', 'rocket-1', 'truck-1'],
    targetObjects: ['tank-1', 'ifv-1', 'artillery-1'],
    type: 'catalog',
    doctrine: 'Chinese',
  },
  {
    id: 'mechanized-infantry',
    name: 'Mechanized Infantry Battalion',
    description: 'Mobile infantry with armored vehicle support',
    objects: ['ifv-1', 'apc-1', 'truck-1', 'artillery-1'],
    targetObjects: ['ifv-1', 'apc-1'],
    type: 'catalog',
    doctrine: 'Chinese',
  },
  {
    id: 'artillery-battery',
    name: 'Artillery Battery',
    description: 'Heavy fire support unit with logistics',
    objects: ['artillery-1', 'rocket-1', 'truck-1'],
    targetObjects: ['artillery-1', 'rocket-1'],
    type: 'catalog',
    doctrine: 'Chinese',
  },
  {
    id: 'logistics-convoy',
    name: 'Logistics Convoy',
    description: 'Supply and transport vehicles with escort',
    objects: ['truck-1', 'apc-1'],
    targetObjects: ['truck-1'],
    type: 'catalog',
    doctrine: 'Chinese',
  },

  // AIR GROUPS (Airspace spawning)
  {
    id: 'air-superiority',
    name: 'Air Superiority Package',
    description: 'Fighter formation with support aircraft',
    objects: ['fighter-1', 'awacs-1', 'transport-1'],
    targetObjects: ['fighter-1'],
    type: 'catalog',
    doctrine: 'Chinese',
  },
  {
    id: 'strategic-strike',
    name: 'Strategic Strike Package',
    description: 'Long-range precision strike capability',
    objects: ['bomber-1', 'fighter-1', 'awacs-1', 'transport-1'],
    targetObjects: ['bomber-1', 'fighter-1'],
    type: 'catalog',
    doctrine: 'Chinese',
  },
  {
    id: 'helicopter-squadron',
    name: 'Attack Helicopter Squadron',
    description: 'Rotary wing close air support formation',
    objects: ['helicopter-1', 'transport-1'],
    targetObjects: ['helicopter-1'],
    type: 'catalog',
    doctrine: 'Chinese',
  },
  {
    id: 'drone-swarm',
    name: 'UAV Reconnaissance Swarm',
    description: 'Unmanned aerial vehicle surveillance formation',
    objects: ['drone-1', 'awacs-1'],
    targetObjects: ['drone-1'],
    type: 'catalog',
    doctrine: 'Chinese',
  },
]

const ObjectSelection: React.FC<ObjectSelectionProps> = ({ onNext }) => {
  // Core State
  const [selectedDomain, setSelectedDomain] = useState<'all' | 'ground' | 'air' | 'sea'>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTags, setSelectedTags] = useState<string[]>([])

  // Group Management
  const [customGroups, setCustomGroups] = useState<ObjectGroup[]>([])
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null)
  const [editingGroupName, setEditingGroupName] = useState<string | null>(null)
  const [groupNameInput, setGroupNameInput] = useState('')

  // Suggestions & UI
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [draggedObject, setDraggedObject] = useState<string | null>(null)
  const [showNewGroupModal, setShowNewGroupModal] = useState(false)
  const [newGroupName, setNewGroupName] = useState('')
  const [showPresetModal, setShowPresetModal] = useState(false)
  const [presetSearchTerm, setPresetSearchTerm] = useState('')
  const [showBiomeWarning, setShowBiomeWarning] = useState(false)
  const [pendingObjectId, setPendingObjectId] = useState<string | null>(null)
  const [biomeConflict, setBiomeConflict] = useState<{ existing: string[]; new: string } | null>(
    null
  )
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false)
  const [pendingObjectFromSuggestion, setPendingObjectFromSuggestion] = useState(false)

  // Get filtered objects for catalog
  const filteredObjects = useMemo(() => {
    return MILITARY_OBJECTS.filter((object) => {
      const matchesDomain =
        selectedDomain === 'all' ||
        (selectedDomain === 'sea' && object.domain === 'naval') ||
        (selectedDomain === 'ground' && object.domain === 'ground') ||
        (selectedDomain === 'air' && object.domain === 'air')

      const matchesSearch =
        object.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        object.subcategory.toLowerCase().includes(searchTerm.toLowerCase()) ||
        object.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()))

      const matchesTags =
        selectedTags.length === 0 || selectedTags.some((tag) => object.tags.includes(tag))

      return matchesDomain && matchesSearch && matchesTags
    })
  }, [selectedDomain, searchTerm, selectedTags])

  // Get filtered preset groups for modal
  const filteredPresetGroups = useMemo(() => {
    return PRESET_GROUPS.filter((group) => {
      const matchesSearch =
        group.name.toLowerCase().includes(presetSearchTerm.toLowerCase()) ||
        group.description.toLowerCase().includes(presetSearchTerm.toLowerCase()) ||
        group.doctrine?.toLowerCase().includes(presetSearchTerm.toLowerCase())
      return matchesSearch
    })
  }, [presetSearchTerm])

  // Helper function to get biome from domain
  const getBiomeFromDomain = (domain: string) => {
    switch (domain) {
      case 'naval':
        return 'Water'
      case 'ground':
        return 'Land'
      case 'air':
        return 'Air'
      default:
        return 'Unknown'
    }
  }

  // Helper function to get biome icon
  const getBiomeIcon = (biome: string, size = 16) => {
    switch (biome) {
      case 'Water':
        return <Waves size={size} />
      case 'Land':
        return <Mountain size={size} />
      case 'Air':
        return <Cloud size={size} />
      default:
        return <Target size={size} />
    }
  }

  // Get biomes for a group
  const getGroupBiomes = (group: ObjectGroup) => {
    const objects = group.objects
      .map((id) => MILITARY_OBJECTS.find((obj) => obj.id === id))
      .filter(Boolean) as MilitaryObject[]
    const biomes = [...new Set(objects.map((obj) => getBiomeFromDomain(obj.domain)))]
    return biomes
  }

  // Get active group
  const activeGroup = customGroups.find((g) => g.id === activeGroupId)

  // Get suggestions for active group
  const suggestions = useMemo(() => {
    if (!activeGroup || activeGroup.objects.length === 0) return []

    const activeObjects = activeGroup.objects
      .map((id) => MILITARY_OBJECTS.find((obj) => obj.id === id)!)
      .filter(Boolean)
    const activeDomains = [...new Set(activeObjects.map((obj) => obj.domain))]
    const activeTags = [...new Set(activeObjects.flatMap((obj) => obj.tags))]

    return MILITARY_OBJECTS.filter((object) => {
      // Don't suggest objects already in group
      if (activeGroup.objects.includes(object.id)) return false

      // Suggest objects from same domain or complementary domains
      const domainMatch =
        activeDomains.includes(object.domain) ||
        (activeDomains.includes('ground') && object.domain === 'air') ||
        (activeDomains.includes('naval') && object.domain === 'air')

      // Suggest objects with similar tags
      const tagMatch = object.tags.some((tag) => activeTags.includes(tag))

      return domainMatch || tagMatch
    }).slice(0, 6) // Limit suggestions
  }, [activeGroup])

  // All unique tags for filtering
  const allTags = useMemo(() => {
    const tags = new Set<string>()
    MILITARY_OBJECTS.forEach((obj) => obj.tags.forEach((tag) => tags.add(tag)))
    return Array.from(tags).sort()
  }, [])

  // Group Management Functions
  const createNewGroup = (name: string, fromPreset?: ObjectGroup) => {
    const newGroup: ObjectGroup = {
      id: `custom-${Date.now()}`,
      name,
      objects: fromPreset?.objects || [],
      targetObjects: fromPreset?.targetObjects || [],
      type: 'my-group',
      description: fromPreset?.description || `Custom detection group: ${name}`,
    }

    setCustomGroups((prev) => [...prev, newGroup])
    setActiveGroupId(newGroup.id)
    // Only show suggestions for empty groups or when manually building
    setShowSuggestions(!fromPreset)
  }

  const addObjectToActiveGroup = (objectId: string) => {
    if (!activeGroupId || !activeGroup) return

    const newObject = MILITARY_OBJECTS.find((obj) => obj.id === objectId)
    if (!newObject) return

    // Check if object is already in group
    if (activeGroup.objects.includes(objectId)) return

    // Check for biome conflicts
    if (activeGroup.objects.length > 0) {
      const existingBiomes = getGroupBiomes(activeGroup)
      const newBiome = getBiomeFromDomain(newObject.domain)

      if (!existingBiomes.includes(newBiome)) {
        // Biome conflict detected - show warning
        setBiomeConflict({ existing: existingBiomes, new: newBiome })
        setPendingObjectId(objectId)
        setPendingObjectFromSuggestion(false)
        setShowBiomeWarning(true)
        return
      }
    }

    // No conflict, add the object
    performAddObject(objectId)
  }

  const performAddObject = (objectId: string, asTarget = true) => {
    setCustomGroups((prev) =>
      prev.map((group) => {
        if (group.id === activeGroupId && !group.objects.includes(objectId)) {
          return {
            ...group,
            objects: [...group.objects, objectId],
            targetObjects: asTarget ? [...group.targetObjects, objectId] : group.targetObjects,
          }
        }
        return group
      })
    )
    setShowSuggestions(true)
  }

  const addSuggestedObject = (objectId: string) => {
    if (!activeGroupId || !activeGroup) return

    const newObject = MILITARY_OBJECTS.find((obj) => obj.id === objectId)
    if (!newObject) return

    // Check if object is already in group
    if (activeGroup.objects.includes(objectId)) return

    // Check for biome conflicts
    if (activeGroup.objects.length > 0) {
      const existingBiomes = getGroupBiomes(activeGroup)
      const newBiome = getBiomeFromDomain(newObject.domain)

      if (!existingBiomes.includes(newBiome)) {
        // Biome conflict detected - show warning
        setBiomeConflict({ existing: existingBiomes, new: newBiome })
        setPendingObjectId(objectId)
        setPendingObjectFromSuggestion(true)
        setShowBiomeWarning(true)
        return
      }
    }

    // Add suggested object as non-target (grey)
    performAddObject(objectId, false)
  }

  const addAllSuggestions = () => {
    if (!activeGroupId || !activeGroup) return

    const compatibleObjects: string[] = []
    const conflictObjects: string[] = []

    // Categorize suggestions based on biome compatibility
    const existingBiomes = activeGroup.objects.length > 0 ? getGroupBiomes(activeGroup) : []

    for (const obj of suggestions) {
      // Skip if already in group
      if (activeGroup.objects.includes(obj.id)) continue

      const objBiome = getBiomeFromDomain(obj.domain)

      if (existingBiomes.length === 0 || existingBiomes.includes(objBiome)) {
        compatibleObjects.push(obj.id)
      } else {
        conflictObjects.push(obj.id)
      }
    }

    // Add all compatible objects
    setCustomGroups((prev) =>
      prev.map((group) => {
        if (group.id === activeGroupId) {
          const newObjects = compatibleObjects.filter((id) => !group.objects.includes(id))
          return {
            ...group,
            objects: [...group.objects, ...newObjects],
            // Don't add to targetObjects - keep them as non-targets (grey)
            targetObjects: group.targetObjects,
          }
        }
        return group
      })
    )

    // Close modal and show results
    setShowSuggestionsModal(false)

    // Show notification about what was added
    if (compatibleObjects.length > 0) {
      setShowSuggestions(true)
    }
  }

  const removeObjectFromActiveGroup = (objectId: string) => {
    if (!activeGroupId) return

    setCustomGroups((prev) =>
      prev.map((group) => {
        if (group.id === activeGroupId) {
          return {
            ...group,
            objects: group.objects.filter((id) => id !== objectId),
            targetObjects: group.targetObjects.filter((id) => id !== objectId),
          }
        }
        return group
      })
    )
  }

  const deleteGroup = (groupId: string) => {
    setCustomGroups((prev) => prev.filter((g) => g.id !== groupId))
    if (activeGroupId === groupId) {
      setActiveGroupId(null)
      setShowSuggestions(false)
    }
  }

  const duplicateGroup = (group: ObjectGroup) => {
    const newGroup: ObjectGroup = {
      ...group,
      id: `custom-${Date.now()}`,
      name: `${group.name} (Copy)`,
    }
    setCustomGroups((prev) => [...prev, newGroup])
  }

  const updateGroupName = (groupId: string, newName: string) => {
    setCustomGroups((prev) =>
      prev.map((group) => (group.id === groupId ? { ...group, name: newName } : group))
    )
  }

  // Drag and Drop Handlers
  const handleDragStart = (objectId: string) => {
    setDraggedObject(objectId)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    if (draggedObject && activeGroupId) {
      addObjectToActiveGroup(draggedObject)
    }
    setDraggedObject(null)
  }

  const getObjectIcon = (object: MilitaryObject) => {
    switch (object.category) {
      case 'ship':
        return <Ship size={20} />
      case 'vehicle':
        return <Truck size={20} />
      case 'aircraft':
        return <Plane size={20} />
      default:
        return <Target size={20} />
    }
  }

  const handleContinue = () => {
    const allSelectedObjects = customGroups.flatMap((group) =>
      group.objects.map((id) => MILITARY_OBJECTS.find((obj) => obj.id === id)!).filter(Boolean)
    )
    onNext(customGroups, allSelectedObjects)
  }

  return (
    <SelectionContainer>
      <Row className="g-3" style={{ margin: 0 }}>
        {/* Left Sidebar - Object Catalog */}
        <Col lg={3}>
          <div
            style={{
              background: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: '12px',
              padding: '1.5rem',
              height: 'calc(100vh - 280px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <h6 className="text-white mb-3">Object Catalog</h6>

            {/* Domain Filter Tabs */}
            <div className="mb-3">
              <div className="d-flex gap-1">
                {['all', 'ground', 'air', 'sea'].map((domain) => (
                  <Button
                    key={domain}
                    variant={selectedDomain === domain ? 'primary' : 'outline-secondary'}
                    size="sm"
                    onClick={() => setSelectedDomain(domain as any)}
                    style={{
                      fontSize: '0.75rem',
                      textTransform: 'capitalize',
                    }}
                  >
                    {domain}
                  </Button>
                ))}
              </div>
            </div>

            {/* Search */}
            <div className="mb-3">
              <InputGroup size="sm">
                <InputGroup.Text style={{ background: '#2a2a2a', border: '1px solid #333' }}>
                  <Search size={14} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search objects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    background: '#2a2a2a',
                    border: '1px solid #333',
                    color: '#fff',
                    fontSize: '0.85rem',
                  }}
                />
              </InputGroup>
            </div>

            {/* Tag Filters */}
            <div className="mb-3">
              <div className="small mb-2" style={{ color: '#ccc' }}>
                Filter by Tags:
              </div>
              <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                {allTags.slice(0, 8).map((tag) => (
                  <Badge
                    key={tag}
                    bg={selectedTags.includes(tag) ? 'primary' : 'secondary'}
                    className="me-1 mb-1"
                    style={{
                      fontSize: '0.6rem',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      setSelectedTags((prev) =>
                        prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
                      )
                    }}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Object List */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <div className="small mb-2" style={{ color: '#ccc' }}>
                {filteredObjects.length} objects
              </div>

              {filteredObjects.map((object) => {
                const isInActiveGroup = activeGroup?.objects.includes(object.id)
                return (
                  <div
                    key={object.id}
                    draggable
                    onDragStart={() => handleDragStart(object.id)}
                    style={{
                      background: isInActiveGroup ? 'rgba(128, 0, 133, 0.2)' : '#2a2a2a',
                      border: `1px solid ${isInActiveGroup ? '#800085' : '#333'}`,
                      borderRadius: '8px',
                      padding: '0.75rem',
                      marginBottom: '0.5rem',
                      cursor: activeGroupId ? 'grab' : 'default',
                      transition: 'all 0.2s',
                    }}
                    onClick={() => activeGroupId && addObjectToActiveGroup(object.id)}
                  >
                    <div className="d-flex align-items-center">
                      <div className="me-2" style={{ color: isInActiveGroup ? '#800085' : '#666' }}>
                        {getObjectIcon(object)}
                      </div>
                      <div className="flex-grow-1">
                        <div
                          className="text-white"
                          style={{ fontSize: '0.85rem', fontWeight: '500' }}
                        >
                          {object.name}
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                          {object.subcategory}
                        </div>
                      </div>
                      {isInActiveGroup && <Check size={14} style={{ color: '#800085' }} />}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </Col>

        {/* Main Panel - Group Builder */}
        <Col lg={7}>
          <div
            style={{
              background: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: '12px',
              height: 'calc(100vh - 280px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Browse Pre-Made Groups */}
            <div style={{ padding: '1.5rem', borderBottom: '1px solid #333' }}>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="text-white mb-0">Pre-Made Groups</h6>
                <Badge bg="secondary">{PRESET_GROUPS.length} available</Badge>
              </div>
              <Button
                variant="outline-primary"
                className="w-100"
                onClick={() => setShowPresetModal(true)}
                style={{
                  background: 'rgba(128, 0, 133, 0.1)',
                  borderColor: '#800085',
                  color: '#fff',
                }}
              >
                <Search size={16} className="me-2" />
                Browse Catalog Groups
              </Button>
              <div className="text-center mt-2">
                <small style={{ color: '#ccc' }}>
                  Ready-to-use detection groups for common scenarios
                </small>
                <div className="d-flex justify-content-center gap-2 mt-2">
                  {['Water', 'Land', 'Air'].map((biome) => (
                    <div
                      key={biome}
                      className="d-flex align-items-center gap-1"
                      style={{
                        color:
                          biome === 'Water' ? '#3b82f6' : biome === 'Land' ? '#16a34a' : '#f59e0b',
                        fontSize: '0.65rem',
                      }}
                    >
                      {getBiomeIcon(biome, 10)}
                      <span>{biome}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Active Group Workspace */}
            <div
              style={{
                flex: 1,
                padding: '1.5rem',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
              }}
            >
              {activeGroup ? (
                <>
                  {/* Group Header */}
                  <div className="d-flex align-items-center mb-3">
                    {editingGroupName === activeGroup.id ? (
                      <div className="d-flex align-items-center">
                        <Form.Control
                          type="text"
                          value={groupNameInput}
                          onChange={(e) => setGroupNameInput(e.target.value)}
                          onBlur={() => {
                            updateGroupName(activeGroup.id, groupNameInput)
                            setEditingGroupName(null)
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              updateGroupName(activeGroup.id, groupNameInput)
                              setEditingGroupName(null)
                            }
                          }}
                          style={{
                            background: '#2a2a2a',
                            border: '1px solid #333',
                            color: '#fff',
                            fontSize: '1.1rem',
                            fontWeight: '600',
                          }}
                          autoFocus
                        />
                      </div>
                    ) : (
                      <>
                        <h5 className="text-white mb-0 me-2">{activeGroup.name}</h5>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            setEditingGroupName(activeGroup.id)
                            setGroupNameInput(activeGroup.name)
                          }}
                          style={{ padding: '0.25rem' }}
                        >
                          <Edit2 size={12} />
                        </Button>
                      </>
                    )}
                    <div className="ms-auto d-flex align-items-center gap-2">
                      {/* Smart Suggestions Button */}
                      {showSuggestions && suggestions.length > 0 && (
                        <Button
                          variant="warning"
                          size="sm"
                          onClick={() => setShowSuggestionsModal(true)}
                          style={{
                            background: 'rgba(245, 158, 11, 0.2)',
                            borderColor: '#f59e0b',
                            color: '#fff',
                            fontSize: '0.75rem',
                            padding: '0.25rem 0.5rem',
                          }}
                        >
                          <Lightbulb size={12} className="me-1" style={{ color: '#f59e0b' }} />
                          Suggestions ({suggestions.length})
                        </Button>
                      )}

                      <Badge bg="primary" className="me-2">
                        {activeGroup.objects.length} objects
                      </Badge>
                      <Badge bg="success">{activeGroup.targetObjects.length} targets</Badge>
                    </div>
                  </div>

                  {/* Drop Zone */}
                  <div
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    style={{
                      flex: 1,
                      border: '2px dashed #333',
                      borderRadius: '8px',
                      padding: '1rem',
                      minHeight: '200px',
                      overflowY: 'auto',
                      background: draggedObject
                        ? 'rgba(128, 0, 133, 0.1)'
                        : 'rgba(42, 42, 42, 0.3)',
                    }}
                  >
                    {activeGroup.objects.length === 0 ? (
                      <div className="text-center text-muted py-5">
                        <Target size={40} className="mb-3" />
                        <div>Drag objects here or click from catalog</div>
                        <div className="small">Build your detection group</div>
                      </div>
                    ) : (
                      <Row className="g-3">
                        {activeGroup.objects.map((objId) => {
                          const obj = MILITARY_OBJECTS.find((o) => o.id === objId)
                          if (!obj) return null
                          const isTarget = activeGroup.targetObjects.includes(objId)

                          return (
                            <Col key={objId} md={6} lg={4}>
                              <div
                                style={{
                                  background: isTarget ? 'rgba(16, 185, 129, 0.1)' : '#2a2a2a',
                                  border: `1px solid ${
                                    isTarget ? 'rgba(16, 185, 129, 0.5)' : '#444'
                                  }`,
                                  borderRadius: '8px',
                                  padding: '12px',
                                  height: '200px',
                                  width: '100%',
                                  minHeight: '200px',
                                  maxHeight: '200px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  transition: 'border-color 0.2s ease',
                                  position: 'relative',
                                  boxSizing: 'border-box',
                                }}
                              >
                                {/* Thumbnail Area - At the top */}
                                <div
                                  style={{
                                    height: '80px',
                                    background: isTarget
                                      ? 'rgba(16, 185, 129, 0.05)'
                                      : 'rgba(255, 255, 255, 0.02)',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginBottom: '12px',
                                    position: 'relative',
                                  }}
                                >
                                  {/* Object Icon */}
                                  <div
                                    style={{
                                      color: isTarget ? '#10b981' : '#666',
                                      fontSize: '2rem',
                                      opacity: 0.7,
                                    }}
                                  >
                                    {getObjectIcon(obj)}
                                  </div>

                                  {/* Trash Button - Overlaid on top-right of thumbnail */}
                                  <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => removeObjectFromActiveGroup(objId)}
                                    style={{
                                      position: 'absolute',
                                      top: '6px',
                                      right: '6px',
                                      width: '24px',
                                      height: '24px',
                                      padding: '0',
                                      color: '#666',
                                      textDecoration: 'none',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      background: 'rgba(0, 0, 0, 0.3)',
                                      borderRadius: '4px',
                                    }}
                                  >
                                    <Trash2 size={12} />
                                  </Button>
                                </div>

                                {/* Content Area */}
                                <div
                                  style={{
                                    height: '76px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                  }}
                                >
                                  {/* Object Name */}
                                  <div
                                    style={{
                                      color: '#ffffff',
                                      fontSize: '0.9rem',
                                      fontWeight: '600',
                                      lineHeight: '1.4',
                                      overflow: 'hidden',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      height: '2.52rem',
                                    }}
                                  >
                                    {obj.name}
                                  </div>

                                  {/* Object Type */}
                                  <div
                                    style={{
                                      color: '#999',
                                      fontSize: '0.8rem',
                                      lineHeight: '1.3',
                                      height: '1.04rem',
                                      overflow: 'hidden',
                                      whiteSpace: 'nowrap',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    {obj.subcategory}
                                  </div>
                                </div>

                                {/* Footer Row - Biome Icon and Target Badge */}
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                  }}
                                >
                                  <div
                                    style={{
                                      color:
                                        getBiomeFromDomain(obj.domain) === 'Water'
                                          ? '#60a5fa'
                                          : getBiomeFromDomain(obj.domain) === 'Land'
                                          ? '#4ade80'
                                          : '#fbbf24',
                                    }}
                                  >
                                    {getBiomeIcon(getBiomeFromDomain(obj.domain), 18)}
                                  </div>

                                  {isTarget && (
                                    <Badge
                                      bg="success"
                                      style={{
                                        fontSize: '0.65rem',
                                        padding: '3px 8px',
                                      }}
                                    >
                                      Target
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </Col>
                          )
                        })}
                      </Row>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center text-muted py-5">
                  <Plus size={40} className="mb-3" />
                  <h6>No Active Group</h6>
                  <p>Create a new group or select one from the right panel</p>
                  <Button variant="primary" onClick={() => setShowNewGroupModal(true)}>
                    <Plus size={16} className="me-2" />
                    Create New Group
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Col>

        {/* Right Sidebar - Group List & Controls */}
        <Col lg={2}>
          <div
            style={{
              background: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: '12px',
              padding: '1rem',
              height: 'calc(100vh - 280px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h6 className="text-white mb-0" style={{ fontSize: '0.9rem' }}>
                My Groups
              </h6>
              <Button variant="primary" size="sm" onClick={() => setShowNewGroupModal(true)}>
                <Plus size={14} />
              </Button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto' }}>
              {customGroups.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <div className="small">No custom groups yet</div>
                  <div className="small">Create one to get started</div>
                </div>
              ) : (
                customGroups.map((group) => (
                  <div
                    key={group.id}
                    style={{
                      background: activeGroupId === group.id ? 'rgba(128, 0, 133, 0.2)' : '#2a2a2a',
                      border: `1px solid ${activeGroupId === group.id ? '#800085' : '#333'}`,
                      borderRadius: '12px',
                      padding: '0.75rem',
                      marginBottom: '0.75rem',
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'all 0.2s ease',
                    }}
                    onClick={() => {
                      setActiveGroupId(group.id)
                      setShowSuggestions(group.objects.length > 0)
                    }}
                    onMouseEnter={(e) => {
                      if (activeGroupId !== group.id) {
                        e.currentTarget.style.background = '#333'
                        e.currentTarget.style.borderColor = '#555'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeGroupId !== group.id) {
                        e.currentTarget.style.background = '#2a2a2a'
                        e.currentTarget.style.borderColor = '#333'
                      }
                    }}
                  >
                    {/* Action Buttons - Top Right Corner */}
                    <div
                      style={{
                        position: 'absolute',
                        top: '0.5rem',
                        right: '0.5rem',
                        display: 'flex',
                        gap: '0.25rem',
                        opacity: 0.8,
                      }}
                    >
                      <Button
                        variant="link"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          duplicateGroup(group)
                        }}
                        style={{
                          padding: '0.25rem',
                          color: '#999',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        title="Duplicate Group"
                      >
                        <Copy size={10} />
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          deleteGroup(group.id)
                        }}
                        style={{
                          padding: '0.25rem',
                          color: '#dc3545',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        title="Delete Group"
                      >
                        <Trash2 size={10} />
                      </Button>
                    </div>

                    {/* Group Name - Full Width */}
                    <div
                      className="text-white fw-bold mb-2"
                      style={{
                        fontSize: '0.85rem',
                        lineHeight: '1.2',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        width: '100%',
                        paddingTop: '1.5rem',
                      }}
                    >
                      {group.name}
                    </div>

                    {/* Stats Row */}
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex gap-1">
                        <Badge bg="primary" style={{ fontSize: '0.65rem', padding: '2px 6px' }}>
                          {group.objects.length} obj
                        </Badge>
                        <Badge bg="success" style={{ fontSize: '0.65rem', padding: '2px 6px' }}>
                          {group.targetObjects.length} tgt
                        </Badge>
                      </div>

                      {/* Biome Icons */}
                      <div className="d-flex gap-1">
                        {getGroupBiomes(group).map((biome) => (
                          <div
                            key={biome}
                            style={{
                              color:
                                biome === 'Water'
                                  ? '#60a5fa'
                                  : biome === 'Land'
                                  ? '#4ade80'
                                  : '#fbbf24',
                            }}
                            title={biome}
                          >
                            {getBiomeIcon(biome, 14)}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {customGroups.length > 0 && (
              <div
                style={{
                  marginTop: 'auto',
                  paddingTop: '1rem',
                  borderTop: '1px solid #333',
                  flexShrink: 0,
                }}
              >
                <Button
                  variant="primary"
                  size="sm"
                  className="w-100"
                  onClick={handleContinue}
                  style={{
                    background: 'linear-gradient(135deg, #800085 0%, #b347b8 100%)',
                    border: 'none',
                    fontSize: '0.8rem',
                    padding: '0.5rem',
                  }}
                >
                  Continue →
                </Button>
                <div className="text-center mt-2">
                  <small style={{ color: '#ccc' }}>
                    {customGroups.length} group{customGroups.length > 1 ? 's' : ''} ready
                  </small>
                </div>
              </div>
            )}
          </div>
        </Col>
      </Row>

      {/* Browse Preset Groups Modal */}
      <Modal
        show={showPresetModal}
        onHide={() => setShowPresetModal(false)}
        data-bs-theme="dark"
        size="lg"
      >
        <Modal.Header style={{ background: '#2a2a2a', borderBottom: '1px solid #333' }} closeButton>
          <Modal.Title>Browse Catalog Groups</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#1a1a1a', maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Search */}
          <Form.Group className="mb-4">
            <InputGroup>
              <InputGroup.Text style={{ background: '#2a2a2a', border: '1px solid #333' }}>
                <Search size={16} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search groups by name, description, or doctrine..."
                value={presetSearchTerm}
                onChange={(e) => setPresetSearchTerm(e.target.value)}
                style={{
                  background: '#2a2a2a',
                  border: '1px solid #333',
                  color: '#fff',
                }}
              />
            </InputGroup>
          </Form.Group>

          {/* Groups Grid */}
          <Row className="g-3">
            {filteredPresetGroups.map((group) => (
              <Col md={6} key={group.id}>
                <Card style={{ background: '#2a2a2a', border: '1px solid #333', height: '100%' }}>
                  <Card.Body className="d-flex flex-column">
                    <div className="d-flex align-items-start justify-content-between mb-2">
                      <Card.Title className="text-white mb-0" style={{ fontSize: '1rem' }}>
                        {group.name}
                      </Card.Title>
                      <Badge bg="info" style={{ fontSize: '0.7rem' }}>
                        {group.objects.length} objects
                      </Badge>
                    </div>

                    <Card.Text className="text-muted small mb-2" style={{ fontSize: '0.85rem' }}>
                      {group.description}
                    </Card.Text>

                    {/* Biome Indicators */}
                    <div className="d-flex align-items-center gap-1 mb-3">
                      <span className="small text-muted me-2">Environment:</span>
                      {getGroupBiomes(group).map((biome) => (
                        <Badge
                          key={biome}
                          style={{
                            background:
                              biome === 'Water'
                                ? '#1e40af'
                                : biome === 'Land'
                                ? '#15803d'
                                : '#d97706',
                            color: 'white',
                            fontSize: '0.65rem',
                          }}
                          className="d-flex align-items-center gap-1"
                        >
                          {getBiomeIcon(biome, 10)}
                          {biome}
                        </Badge>
                      ))}
                    </div>

                    {/* Object Icons Preview */}
                    <div className="d-flex align-items-center mb-3" style={{ minHeight: '24px' }}>
                      {group.objects.slice(0, 6).map((objId) => {
                        const obj = MILITARY_OBJECTS.find((o) => o.id === objId)
                        return obj ? (
                          <div key={objId} className="me-2" style={{ color: '#666' }}>
                            {React.cloneElement(getObjectIcon(obj), { size: 16 })}
                          </div>
                        ) : null
                      })}
                      {group.objects.length > 6 && (
                        <span className="text-muted small">+{group.objects.length - 6} more</span>
                      )}
                    </div>

                    <div className="d-flex align-items-center justify-content-between mt-auto">
                      {group.doctrine && (
                        <Badge bg="dark" className="small">
                          {group.doctrine} Doctrine
                        </Badge>
                      )}
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          createNewGroup(`${group.name} (Copy)`, group)
                          setShowPresetModal(false)
                          setPresetSearchTerm('')
                        }}
                        style={{
                          background: 'linear-gradient(135deg, #800085 0%, #b347b8 100%)',
                          border: 'none',
                          fontSize: '0.8rem',
                        }}
                      >
                        Use This Group
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          {filteredPresetGroups.length === 0 && (
            <div className="text-center py-5">
              <div className="text-muted mb-2">No groups found</div>
              <div className="small text-muted">Try adjusting your search terms</div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ background: '#2a2a2a', borderTop: '1px solid #333' }}>
          <div className="w-100 d-flex justify-content-between align-items-center">
            <small className="text-muted">
              {filteredPresetGroups.length} of {PRESET_GROUPS.length} groups shown
            </small>
            <Button
              variant="secondary"
              onClick={() => {
                setShowPresetModal(false)
                setPresetSearchTerm('')
              }}
            >
              Close
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Biome Warning Modal */}
      <Modal
        show={showBiomeWarning}
        onHide={() => setShowBiomeWarning(false)}
        data-bs-theme="dark"
        centered
      >
        <Modal.Header style={{ background: '#2a2a2a', borderBottom: '1px solid #333' }} closeButton>
          <Modal.Title className="d-flex align-items-center">
            <AlertTriangle size={20} className="me-2" style={{ color: '#f59e0b' }} />
            Environment Mismatch Detected
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#1a1a1a' }}>
          {biomeConflict && (
            <>
              <div className="mb-3">
                <p className="text-white mb-3">
                  You're trying to mix objects from different environments in one group:
                </p>

                <div className="mb-3">
                  <div className="small text-muted mb-2">Current group environment(s):</div>
                  <div className="d-flex gap-2">
                    {biomeConflict.existing.map((biome) => (
                      <Badge
                        key={biome}
                        style={{
                          background:
                            biome === 'Water'
                              ? '#1e40af'
                              : biome === 'Land'
                              ? '#15803d'
                              : '#d97706',
                          color: 'white',
                        }}
                        className="d-flex align-items-center gap-1"
                      >
                        {getBiomeIcon(biome, 14)}
                        {biome}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="mb-3">
                  <div className="small text-muted mb-2">Object you're trying to add:</div>
                  <Badge
                    style={{
                      background:
                        biomeConflict.new === 'Water'
                          ? '#1e40af'
                          : biomeConflict.new === 'Land'
                          ? '#15803d'
                          : '#d97706',
                      color: 'white',
                    }}
                    className="d-flex align-items-center gap-1"
                  >
                    {getBiomeIcon(biomeConflict.new, 14)}
                    {biomeConflict.new}
                  </Badge>
                </div>

                <div
                  className="p-3 rounded"
                  style={{
                    background: 'rgba(245, 158, 11, 0.1)',
                    border: '1px solid rgba(245, 158, 11, 0.3)',
                  }}
                >
                  <div className="small text-muted">
                    <strong>Why this matters:</strong> Mixed-environment groups may not deploy
                    properly in Step 2 when selecting spawn locations. For example, naval vessels
                    can't spawn on land, and ground vehicles can't spawn in water.
                  </div>
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={{ background: '#2a2a2a', borderTop: '1px solid #333' }}>
          <Button
            variant="secondary"
            onClick={() => {
              setShowBiomeWarning(false)
              setPendingObjectId(null)
              setBiomeConflict(null)
              setPendingObjectFromSuggestion(false)
            }}
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={() => {
              if (pendingObjectId) {
                performAddObject(pendingObjectId, !pendingObjectFromSuggestion)
              }
              setShowBiomeWarning(false)
              setPendingObjectId(null)
              setBiomeConflict(null)
              setPendingObjectFromSuggestion(false)
            }}
          >
            Add Anyway
          </Button>
        </Modal.Footer>
      </Modal>

      {/* New Group Modal */}
      <Modal
        show={showNewGroupModal}
        onHide={() => setShowNewGroupModal(false)}
        data-bs-theme="dark"
      >
        <Modal.Header style={{ background: '#2a2a2a', borderBottom: '1px solid #333' }} closeButton>
          <Modal.Title>Create New Group</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#1a1a1a' }}>
          <Form.Group>
            <Form.Label>Group Name</Form.Label>
            <Form.Control
              type="text"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="e.g. My Detection Group"
              style={{
                background: '#2a2a2a',
                border: '1px solid #333',
                color: '#fff',
              }}
              autoFocus
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ background: '#2a2a2a', borderTop: '1px solid #333' }}>
          <Button variant="secondary" onClick={() => setShowNewGroupModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (newGroupName.trim()) {
                createNewGroup(newGroupName.trim())
                setNewGroupName('')
                setShowNewGroupModal(false)
              }
            }}
            disabled={!newGroupName.trim()}
          >
            Create Group
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Smart Suggestions Modal */}
      <Modal
        show={showSuggestionsModal}
        onHide={() => setShowSuggestionsModal(false)}
        data-bs-theme="dark"
        size="lg"
      >
        <Modal.Header style={{ background: '#2a2a2a', borderBottom: '1px solid #333' }} closeButton>
          <Modal.Title className="d-flex align-items-center">
            <Lightbulb size={20} className="me-2" style={{ color: '#f59e0b' }} />
            Smart Suggestions
            <OverlayTrigger
              overlay={
                <Tooltip>
                  AI-powered recommendations based on military doctrine and operational context
                </Tooltip>
              }
            >
              <Info size={16} className="ms-2" style={{ color: '#666', cursor: 'help' }} />
            </OverlayTrigger>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#1a1a1a', maxHeight: '60vh', overflowY: 'auto' }}>
          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <p className="text-muted small mb-0">
                Based on your current group composition, we recommend adding these complementary
                objects:
              </p>
              {suggestions.length > 0 && (
                <Button
                  variant="warning"
                  size="sm"
                  onClick={addAllSuggestions}
                  style={{
                    background: 'rgba(245, 158, 11, 0.2)',
                    borderColor: '#f59e0b',
                    color: '#fff',
                    fontSize: '0.75rem',
                    padding: '0.375rem 0.75rem',
                  }}
                >
                  <Plus size={12} className="me-1" />
                  Add All ({suggestions.length})
                </Button>
              )}
            </div>
          </div>

          <Row className="g-3">
            {suggestions.map((obj) => (
              <Col md={6} lg={4} key={obj.id}>
                <div
                  style={{
                    background: 'rgba(245, 158, 11, 0.1)',
                    border: '1px solid #f59e0b',
                    borderRadius: '8px',
                    padding: '12px',
                    height: '200px',
                    width: '100%',
                    minHeight: '200px',
                    maxHeight: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                  }}
                  onClick={() => {
                    addSuggestedObject(obj.id)
                    setShowSuggestionsModal(false)
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(245, 158, 11, 0.2)'
                    e.currentTarget.style.transform = 'translateY(-2px)'
                    e.currentTarget.style.borderColor = '#fbbf24'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(245, 158, 11, 0.1)'
                    e.currentTarget.style.transform = 'translateY(0)'
                    e.currentTarget.style.borderColor = '#f59e0b'
                  }}
                >
                  {/* Thumbnail Area - At the top */}
                  <div
                    style={{
                      height: '80px',
                      background: 'rgba(245, 158, 11, 0.05)',
                      borderRadius: '6px',
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '12px',
                      position: 'relative',
                    }}
                  >
                    {/* Object Icon */}
                    <div
                      style={{
                        color: '#f59e0b',
                        fontSize: '2rem',
                        opacity: 0.8,
                      }}
                    >
                      {getObjectIcon(obj)}
                    </div>

                    {/* Add Button - Overlaid on top-right of thumbnail */}
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation()
                        addSuggestedObject(obj.id)
                        setShowSuggestionsModal(false)
                      }}
                      style={{
                        position: 'absolute',
                        top: '6px',
                        right: '6px',
                        width: '24px',
                        height: '24px',
                        padding: '0',
                        background: 'rgba(245, 158, 11, 0.9)',
                        borderColor: '#f59e0b',
                        color: '#fff',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '4px',
                      }}
                    >
                      <Plus size={12} />
                    </Button>
                  </div>

                  {/* Content Area */}
                  <div
                    style={{
                      height: '76px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    {/* Object Name */}
                    <div
                      style={{
                        color: '#ffffff',
                        fontSize: '0.9rem',
                        fontWeight: '600',
                        lineHeight: '1.4',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        height: '2.52rem',
                      }}
                    >
                      {obj.name}
                    </div>

                    {/* Object Type */}
                    <div
                      style={{
                        color: '#ccc',
                        fontSize: '0.8rem',
                        lineHeight: '1.3',
                        height: '1.04rem',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {obj.subcategory}
                    </div>
                  </div>

                  {/* Footer Row - Biome Icon and Suggestion Badge */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div
                      style={{
                        color:
                          getBiomeFromDomain(obj.domain) === 'Water'
                            ? '#60a5fa'
                            : getBiomeFromDomain(obj.domain) === 'Land'
                            ? '#4ade80'
                            : '#fbbf24',
                      }}
                    >
                      {getBiomeIcon(getBiomeFromDomain(obj.domain), 18)}
                    </div>

                    <Badge
                      style={{
                        fontSize: '0.65rem',
                        padding: '3px 8px',
                        background: 'rgba(245, 158, 11, 0.8)',
                        color: '#fff',
                        border: 'none',
                      }}
                    >
                      Suggested
                    </Badge>
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          {suggestions.length === 0 && (
            <div className="text-center py-4">
              <div className="text-muted mb-2">No suggestions available</div>
              <div className="small text-muted">
                Add some objects to your group to get AI-powered recommendations
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ background: '#2a2a2a', borderTop: '1px solid #333' }}>
          <div className="w-100 d-flex justify-content-between align-items-center">
            <small className="text-muted">
              {suggestions.length} recommendations based on current group
            </small>
            <Button variant="secondary" onClick={() => setShowSuggestionsModal(false)}>
              Close
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </SelectionContainer>
  )
}

export default ObjectSelection
